import { type Key, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@gonfalon/async/useDebouncedValue';
import { type SdkKey, ALL_SDK_KEYS, POPULAR_SDK_KEYS } from '@gonfalon/sdk-info/sdkInfo';
import {
  Button,
  Group,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  SearchField,
  Text,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { trackSdkSetupFilterSelected, trackSdkSetupNoResultsFound, trackSdkSetupSdkSelected } from '../../analytics';
import { type FilterCategory, filterDisplayNames, filterSdks } from '../../utils';

import { SdkItem } from './SdkItem';

import styles from './ChooseSDK.module.css';

export function ChooseSDK({ onSelect, selectedSdk }: { onSelect: (sdk: SdkKey) => void; selectedSdk?: SdkKey }) {
  const [searchValue, setSearchValue] = useState('');
  const [filterValue, setFilterValue] = useState<FilterCategory>('all');

  const debouncedSearchValue = useDebouncedValue(searchValue, 300);

  const unpopularSdkKeys = useMemo(
    () => ALL_SDK_KEYS.filter((sdk) => !(POPULAR_SDK_KEYS as readonly SdkKey[]).includes(sdk)),
    [],
  );

  const filteredPopularSdks = useMemo(
    () => filterSdks(POPULAR_SDK_KEYS, filterValue, searchValue),
    [searchValue, filterValue],
  );

  const filteredUnpopularSdks = useMemo(
    () => filterSdks(unpopularSdkKeys, filterValue, searchValue, { sort: true }),
    [unpopularSdkKeys, searchValue, filterValue],
  );

  const hasNoResults = filteredPopularSdks.length === 0 && filteredUnpopularSdks.length === 0;

  useEffect(() => {
    // Only track when debounced value has caught up to current search value
    // and the search term truly doesn't exist in ANY SDK (not just filtered out)
    if (debouncedSearchValue === searchValue && debouncedSearchValue) {
      const matchesInAll = filterSdks(POPULAR_SDK_KEYS, 'all', debouncedSearchValue);
      const matchesInAllUnpopular = filterSdks(unpopularSdkKeys, 'all', debouncedSearchValue, { sort: true });

      if (matchesInAll.length === 0 && matchesInAllUnpopular.length === 0) {
        trackSdkSetupNoResultsFound(debouncedSearchValue);
      }
    }
  }, [debouncedSearchValue, searchValue, unpopularSdkKeys]);

  const handleSdkClick = (sdk: SdkKey) => {
    trackSdkSetupSdkSelected(sdk);
    onSelect(sdk);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <Heading className={styles.title}>Choose SDK</Heading>
        </div>
        <div className={styles.searchContainer}>
          <SearchField aria-label="Search SDKs" value={searchValue} onChange={setSearchValue}>
            <Group>
              <Icon name="search" size="small" />
              <Input placeholder="Search..." />
              {searchValue && (
                <IconButton
                  aria-label="clear"
                  icon="cancel-circle-outline"
                  size="small"
                  variant="minimal"
                  onPress={() => setSearchValue('')}
                />
              )}
            </Group>
          </SearchField>
        </div>
        <div className={styles.filterContainer}>
          <MenuTrigger>
            <Button variant="default" className={styles.filterButton} aria-label="Filter SDKs by type">
              <Icon name="filter-list" />
              <Text className={styles.filterText}>{filterDisplayNames[filterValue]}</Text>
              <Icon name="chevron-down" />
            </Button>
            <Popover className={styles.filterPopover}>
              <Menu
                className={styles.filterMenu}
                onAction={(key: Key) => {
                  const newFilter = key as FilterCategory;
                  trackSdkSetupFilterSelected(newFilter);
                  setFilterValue(newFilter);
                }}
                selectedKeys={[filterValue]}
                data-theme="default"
              >
                <MenuSection>
                  <MenuItem key="all" id="all">
                    All SDKs
                  </MenuItem>
                </MenuSection>
                <MenuSection>
                  <MenuItem key="backendAndServer" id="backendAndServer">
                    Backend and server
                  </MenuItem>
                  <MenuItem key="desktop" id="desktop">
                    Desktop
                  </MenuItem>
                  <MenuItem key="edgeAndCloud" id="edgeAndCloud">
                    Edge and cloud
                  </MenuItem>
                  <MenuItem key="mobile" id="mobile">
                    Mobile
                  </MenuItem>
                  <MenuItem key="webAndFrontend" id="webAndFrontend">
                    Web and frontend
                  </MenuItem>
                </MenuSection>
                <MenuSection>
                  <MenuItem key="observabilitySupported" id="observabilitySupported">
                    Observability supported
                  </MenuItem>
                </MenuSection>
              </Menu>
            </Popover>
          </MenuTrigger>
        </div>
      </div>
      <div className={styles.list}>
        {filteredPopularSdks.length > 0 && (
          <div className={styles.popularSection}>
            <Text className={styles.sectionTitle} size="small">
              Most popular SDKs
            </Text>
            {filteredPopularSdks.map((sdk) => (
              <SdkItem key={sdk} sdk={sdk} isSelected={selectedSdk === sdk} onPress={handleSdkClick} />
            ))}
          </div>
        )}
        {filteredUnpopularSdks.length > 0 && (
          <div className={styles.otherSection}>
            <Text className={styles.sectionTitle} size="small">
              All SDKs
            </Text>
            {filteredUnpopularSdks.map((sdk) => (
              <SdkItem key={sdk} sdk={sdk} isSelected={selectedSdk === sdk} onPress={handleSdkClick} />
            ))}
          </div>
        )}
        {hasNoResults && (
          <Text className={styles.noResults} size="medium">
            No results found
          </Text>
        )}
      </div>
    </div>
  );
}
